import { Alarm } from '@services/alarm'
import {
  Device,
  IStatisticalByDeviceType,
  useDeviceInfos,
  useSessionDeviceStatistical,
} from '@services/device'
import { ISession, useSessionTree } from '@services/session'
import { InnmaxMapTool, useInnmaxMapInstance } from '@shared/InnmaxMap2'
import { IInnmaxMapInstance } from '@shared/InnmaxMap2/map.types'
import moment from 'moment'
import { isEmpty, isNil } from 'ramda'
import React from 'react'

type ISelectedChargingPoint = {
  sideMenuMode: 'main' | 'secondary'
  chargingPoint: any
}

type IMapDashboardContext = {
  currentSession: ISession | null
  setCurrentSession: (session: ISession) => void
  sessionDevices: Device[]
  sessionDeviceStatistical: Record<string, IStatisticalByDeviceType[]>
  setSessionDevices: (devices: Device[]) => void
  sessionsById: Record<string, ISession>
  mapInstance: IInnmaxMapInstance
  queryDeviceInfos: (variables: any, keepPreviousQuery?: boolean) => void
  videoPlayer: { [id: string]: { device: Device } }
  closeVideoPlayer: (device: Device) => void
  addVideoPlayer: (device: Device) => void
  isFilterActived?: boolean
  setFilterVariables: (variables?: Record<string, any>) => void
  filterVariables?: Record<string, any>
  lastUpdated: moment.Moment
  baseLayer?: ISession
  loadingDevices?: boolean
  setSelectedChargingPoint: (
    chargingPoint: ISelectedChargingPoint | null
  ) => void
  selectedChargingPoint?: ISelectedChargingPoint | null
  deviceCountInfoBySessionId?: Record<string, IStatisticalByDeviceType[]>
  isLoadDeviceCountInfo: boolean
  getSessionDeepStatistical: (
    session?: ISession | null,
    statisticalSource?: Record<string, IStatisticalByDeviceType[]> | null
  ) => IStatisticalByDeviceType[]
  selectedAlarm?: Alarm
  setSelectedAlarm: (alarm?: Alarm) => void
  parkingLotViewLoading?: boolean
  setParkingLotViewLoading?: (loading: boolean) => void
  viewMode?: 'map' | 'parkingLot'
}

const MapDashboardContext = React.createContext<IMapDashboardContext>({
  currentSession: null,
  setCurrentSession: (session: ISession) => {},
  sessionDevices: [],
  setSessionDevices: (devices: Device[]) => {},
  sessionsById: {},
  mapInstance: {} as IInnmaxMapInstance,
  queryDeviceInfos: (variables: any, keepPreviousQuery?: boolean) => {},
  videoPlayer: {},
  closeVideoPlayer: (device: Device) => {},
  addVideoPlayer: (device: Device) => {},
  isFilterActived: false,
  setFilterVariables: (variables?: Record<string, any>) => {},
  filterVariables: undefined,
  lastUpdated: moment(),
  setSelectedChargingPoint: (
    chargingPoint: ISelectedChargingPoint | null
  ) => {},
  sessionDeviceStatistical: {},
  isLoadDeviceCountInfo: false,
  getSessionDeepStatistical: (
    session?: ISession | null,
    statisticalSource?: Record<string, IStatisticalByDeviceType[]> | null
  ) => {
    return []
  },
  setSelectedAlarm: () => ({}),
})

// 建立 map dashboard 的 provider，將 map dashboard 的 context 提供給子元件使用
const MapDashboardProvider = (props: React.PropsWithChildren<{}>) => {
  const { children } = props

  const [mapInstance] = useInnmaxMapInstance()

  const [videoPlayer, setVideoPlayer] = React.useState<{
    [id: string]: { device: Device }
  }>({})

  const [filterVariables, _setFilterVariables] = React.useState<
    Record<string, any> | undefined
  >(undefined)
  const [lastUpdated, setLastUpdated] = React.useState<moment.Moment>(moment())
  const [selectedChargingPoint, setSelectedChargingPoint] =
    React.useState<ISelectedChargingPoint | null>(null)

  const [parkingLotViewLoading, setParkingLotViewLoading] =
    React.useState<boolean>(false)
  const [viewMode, setViewMode] = React.useState<'map' | 'parkingLot'>('map')

  const [selectedAlarm, setSelectedAlarm] = React.useState<Alarm | undefined>(
    undefined
  )

  const { baseLayer, byId, loading: loadingSessionTree } = useSessionTree()
  const {
    queryDeviceInfos,
    sessionDeviceStatistical,
    dataSource: devices,
    loading: loadingDevices,
  } = useDeviceInfos()

  const isFetchedBaseLayerStatistical = React.useRef(false)
  const baseQueryRef = React.useRef<any>(null)

  const {
    refetch,
    isLoading: isLoadDeviceCountInfo,
    deviceCountInfoBySessionId,
    getSessionDeepStatistical,
  } = useSessionDeviceStatistical()

  const [currentSession, _setCurrentSession] = React.useState<ISession | null>(
    null
  )
  const [sessionDevices, setSessionDevices] = React.useState<Device[]>([])

  const previousSession = React.useRef<ISession | null>(null)

  const setCurrentSession = React.useCallback((session: ISession) => {
    setSessionDevices([])
    _setCurrentSession({ ...session })
  }, [])

  React.useEffect(() => {
    if (!currentSession?.id || !baseLayer?.id) {
      return
    }
    setViewMode(currentSession.id === baseLayer.id ? 'map' : 'parkingLot')
  }, [currentSession, baseLayer])

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setLastUpdated(moment())
    }, 60 * 1000)
    return () => clearInterval(intervalId)
  }, [])

  React.useEffect(() => {
    if (loadingSessionTree) {
      return
    }
    if (!baseLayer) {
      return
    }
    if (isNil(currentSession) || isEmpty(currentSession)) {
      setCurrentSession(baseLayer)
    }
  }, [baseLayer, currentSession, loadingSessionTree, setCurrentSession])

  React.useEffect(() => {
    if (isFetchedBaseLayerStatistical.current) {
      return
    }

    if (!baseLayer || !baseLayer.id) {
      return
    }

    baseQueryRef.current = {
      sessionId: baseLayer.id,
      deep: true,
      isPositioned: true,
    }

    refetch(baseQueryRef.current, false)
    isFetchedBaseLayerStatistical.current = true
  }, [baseLayer])

  const setFilterVariables = React.useCallback(
    (variables?: Record<string, any>) => {
      if (isNil(variables) || isEmpty(variables)) {
        _setFilterVariables(undefined)
        refetch(baseQueryRef.current, false)
        currentSession?.sourceType === 'parkingLot' &&
          queryDeviceInfos(
            {
              sessionId: currentSession?.id,
              isPositioned: true,
              deep: true,
            },
            false
          )
        return
      }
      const { filterSession, deviceType, ...rest } = variables || {}
      let queryVariables = rest
      queryVariables = {
        ...queryVariables,
        sessionId: filterSession.id,
      }
      _setFilterVariables(queryVariables)
      if (filterSession && filterSession.id !== currentSession?.id) {
        // 有變更 session 時更新當前 session，等 session 在 useEffect 中更新
        setCurrentSession(filterSession)
        return
      }
      if (currentSession?.sourceType === 'parkingLot') {
        queryDeviceInfos(queryVariables, false)
        refetch(queryVariables, false)
      } else {
        refetch(queryVariables, false)
      }
    },
    [currentSession]
  )

  React.useEffect(() => {
    previousSession.current = currentSession
  }, [currentSession])

  React.useEffect(() => {
    // 當前 session 變更時且有設定 filterVariables 時，重新查詢
    if (previousSession.current?.id === currentSession?.id) {
      return
    }
    if (isNil(filterVariables) || isEmpty(filterVariables)) {
      return
    }
    if (currentSession?.sourceType === 'parkingLot') {
      queryDeviceInfos(filterVariables, false)
      refetch(filterVariables, false)
    } else {
      refetch(filterVariables, false)
    }
  }, [currentSession])

  React.useEffect(() => {
    // 當前 session 變更且是停車站點時，需查詢設備資訊 API
    if (loadingDevices) {
      return
    }
    if (currentSession?.sourceType !== 'parkingLot') {
      return
    }
    queryDeviceInfos(
      {
        sessionId: currentSession?.id,
        isPositioned: true,
        deep: true,
      },
      false
    )
  }, [currentSession])

  React.useEffect(() => {
    if (loadingDevices) {
      return
    }
    if (isNil(currentSession) || isNil(devices) || devices.length === 0) {
      setSessionDevices([])
      return
    }
    const sessionDevices = devices.filter(device =>
      InnmaxMapTool.isDevicePositioned(currentSession, device)
    )
    setSessionDevices(sessionDevices || [])
  }, [devices])

  const contextValue = React.useMemo<IMapDashboardContext>(() => {
    const closeVideoPlayer = (device: Device) => {
      const newItems = { ...videoPlayer }
      delete newItems[`${device.deviceType}-${device.id}`]
      return setVideoPlayer(newItems)
    }

    const addVideoPlayer = (device: Device) => {
      return setVideoPlayer({
        ...videoPlayer,
        [`${device.deviceType}-${device.id}`]: {
          device,
        },
      })
    }

    return {
      currentSession,
      setCurrentSession,
      sessionDevices,
      setSessionDevices,
      sessionsById: byId,
      mapInstance,
      queryDeviceInfos,
      videoPlayer,
      closeVideoPlayer,
      addVideoPlayer,
      isFilterActived: !isEmpty(filterVariables) && !isNil(filterVariables),
      setFilterVariables,
      filterVariables,
      lastUpdated,
      baseLayer,
      loadingDevices,
      setSelectedChargingPoint,
      selectedChargingPoint,
      sessionDeviceStatistical,
      isLoadDeviceCountInfo,
      deviceCountInfoBySessionId,
      getSessionDeepStatistical,
      selectedAlarm,
      setSelectedAlarm,
      parkingLotViewLoading,
      setParkingLotViewLoading,
      viewMode,
    }
  }, [
    currentSession,
    sessionDevices,
    byId,
    mapInstance,
    videoPlayer,
    filterVariables,
    lastUpdated,
    baseLayer,
    loadingDevices,
    selectedChargingPoint,
    sessionDeviceStatistical,
    isLoadDeviceCountInfo,
    deviceCountInfoBySessionId,
    getSessionDeepStatistical,
    selectedAlarm,
    parkingLotViewLoading,
    viewMode,
  ])

  return (
    <MapDashboardContext.Provider value={contextValue}>
      {children}
    </MapDashboardContext.Provider>
  )
}

const useDashboardContext = () => {
  const context = React.useContext(MapDashboardContext)
  if (!context) {
    throw new Error(
      'useDashboardContext must be used within a MapDashboardProvider'
    )
  }
  return context
}

export { MapDashboardContext, MapDashboardProvider, useDashboardContext }
